import ScrollToTopButton from '@/components/tv/components/ScrollToTopButton'
import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { VodRowViewProps } from '@/types/vod/vodRowType'
import { useVODStore } from '@/shared/store'
import { pageType } from '@/shared/store/types'
import {
    vodItemImageHeight,
    vodZeroRowItemImageHeight,
} from '@/shared/constants/vodConstants'
import { VodItemsProvider } from '@/features/VOD/context/UseGetVodItemContext/VodItemsContext'
import { ZERO_ROW } from '@/shared/constants'

import {
    FocusContext,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import styled from 'styled-components'
import VodRowTV from '@/appTV/VOD/components/VodRowTV'
import ZeroRowView from '@/clients/streamiq/features/VOD/components/ZeroRowView'
import {
    DIRECTION_LEFT_FOCUS_KEY,
    DIRECTION_RIGHT_FOCUS_KEY,
    VOD_ROW_CONTAINER_FOCUS_KEY,
} from '@/shared/constants/focusLibraryConstants'
import useEnterPageLogAnalytics from '@/shared/hooks/useEnterPageLogAnalytics'
import { homeRowId } from '@/shared/constants/seriesConstants'
import { useSeriesScreen } from '@/clients/streamiq/pages/hooks/useSeriesScreenHook'
import { useAtmospheric } from '@/features/VOD/hooks/useAtmospheric'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui'

const VodGridContentWrapper = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`
const VodGridScrollingRows = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 1;
    flex-grow: 1;
`

export const SeriesRowContainerTV = () => {
    const homeContainerRef = useRef<HTMLDivElement | null>(null)
    useEnterPageLogAnalytics(homeRowId)

    const containerStyle = {
        height: '90vh',
        overflowY: 'auto',
        display: 'flex',
        gap: '50px',
        flexDirection: 'column',
        // padding: '30px',
        // '& > :first-of-type': {
        //     marginBottom: '-50px', // hack to reduce flex gap on first element
        // },
    }

    const scrollToTop = () => {
        const container = homeContainerRef.current
        if (container)
            container?.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
    }
    const seriesRowsRef = useRef<HTMLDivElement[]>([])
    const { seriesRows } = useSeriesScreen()
    const { setVodPages } = useVODStore()

    const handleGoToCategoryButtonClick = (
        itemId: string,
        title: string,
        type: pageType
    ): void => {
        setVodPages(itemId, title, type)
        homeContainerRef.current?.scrollTo(0, 0)
    }

    const { rowElements, handleScroll, handleRefsUpdate } = useAtmospheric(
        homeContainerRef,
        seriesRows || []
    )

    useEffect(() => {
        handleRefsUpdate(seriesRowsRef.current)
    }, [seriesRows?.length, handleRefsUpdate, seriesRowsRef.current])

    useEffect(() => {
        const container = homeContainerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [rowElements, homeContainerRef.current])

    const { ref, focusSelf } = useFocusable({
        focusKey: VOD_ROW_CONTAINER_FOCUS_KEY,
        isFocusBoundary: true,
        focusBoundaryDirections: [
            DIRECTION_LEFT_FOCUS_KEY,
            DIRECTION_RIGHT_FOCUS_KEY,
        ],
    })

    const onRowFocus = useCallback(
        ({ top, height }: { top: number; height: number }) => {
            ref.current.scrollTo({
                top: top - height + 200,
                behavior: 'smooth',
            })
        },
        [ref]
    )

    const childReady = useCallback(() => {
        focusSelf()
    }, [])

    const ZeroRow = useMemo(() => {
        const zeroRow = seriesRows.find(
            (row: VodRowViewProps) => row.title.toLowerCase() === ZERO_ROW
        )
        if (!zeroRow) return <></>

        const {
            productId,
            query,
            serviceName,
            hideCaptions,
            hideProgress,
            rowHeightPercentage,
        } = zeroRow

        return (
            <Box
                key={productId}
                data-index={productId}
                ref={(listElement: HTMLDivElement) => {
                    seriesRowsRef.current[1] = listElement
                }}>
                <VodItemsProvider
                    productId={String(productId)}
                    type={query || ''}
                    serviceName={serviceName || ''}>
                    <ZeroRowView
                        productId={productId}
                        hideCaptions={hideCaptions}
                        hideProgress={hideProgress}
                        rowHeightPercentage={rowHeightPercentage}
                        maxHeight={vodZeroRowItemImageHeight}
                        handleGoToCategoryButtonClick={
                            handleGoToCategoryButtonClick
                        }
                        isHomeContentRow={false}
                    />
                </VodItemsProvider>
            </Box>
        )
    }, [seriesRows, handleGoToCategoryButtonClick])

    const contentToDisplay = useMemo(() => {
        if (!seriesRows) return null

        return seriesRows.map((row: VodRowViewProps, index: number) => {
            const {
                productId,
                title,
                hideCaptions,
                hideProgress,
                rowHeightPercentage,
                hideTitle,
                serviceName,
                cardRadiusDp,
            } = row

            if (title.toLowerCase() !== ZERO_ROW)
                return (
                    <Box
                        key={productId}
                        data-index={row.productId}
                        ref={(listElement: HTMLDivElement) => {
                            seriesRowsRef.current[index] = listElement
                        }}>
                        <VodItemsProvider
                            productId={String(row.productId)}
                            type={row.query || ''}
                            serviceName={serviceName || ''}>
                            <VodRowTV
                                hideCaptions={!!hideCaptions}
                                hideProgress={!!hideProgress}
                                cardRadiusDp={cardRadiusDp}
                                rowHeightPercentage={rowHeightPercentage}
                                maxHeight={vodItemImageHeight}
                                productId={productId}
                                title={hideTitle ? '' : title}
                                handleGoToCategoryButtonClick={
                                    handleGoToCategoryButtonClick
                                }
                                onFocusProp={onRowFocus}
                                onReady={childReady}
                                isHomeContentRow={false}
                            />
                        </VodItemsProvider>
                    </Box>
                )
        })
    }, [seriesRows, handleGoToCategoryButtonClick])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box sx={containerStyle} ref={homeContainerRef}>
                <FocusContext.Provider value={VOD_ROW_CONTAINER_FOCUS_KEY}>
                    <VodGridContentWrapper>
                        <VodGridScrollingRows ref={ref}>
                            {ZeroRow}

                            {/*<Container maxWidth="xl" disableGutters>*/}
                            <Box sx={{ padding: '0 30px 0 30px' }}>
                                {contentToDisplay}
                            </Box>
                            {/*</Container>*/}
                        </VodGridScrollingRows>
                    </VodGridContentWrapper>
                </FocusContext.Provider>

                <ScrollToTopButton
                    handleOnClick={scrollToTop}
                    container={homeContainerRef.current}
                />
            </Box>
        </ErrorBoundary>
    )
}

SeriesRowContainerTV.displayName = 'SeriesRowContainerTV'
